// @owners { team: patient-health }
import { COLORS, SPACING } from '@alto/design-library-tokens';
// eslint-disable-next-line import/no-deprecated
import { getScreenSize } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import {
  Button,
  FlexColumn,
  FlexRow,
  H3,
  InputTextArea,
  Label,
  RadioGroup,
  RadioOption,
  Text,
  spacing,
} from '@alto/pocky';
// eslint-disable-next-line @alto/prefer-react-hook-form
import { Formik } from 'formik';
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import PageHeader from '~web/components/PageHeader';
import logoAlto from '~web/images/altoLogo@2x.png';

export type FormValues = Record<number, string | null | undefined>;

export type Question = {
  order: number;
  question: string;
  required: boolean;
  response: string | null | undefined;
  type: 'numeric' | 'text';
};

type Props = {
  readonly changeStep: (direction?: string) => void;
  readonly onSubmit: (values: FormValues) => void;
  readonly question: Question;
  readonly step: number;
  readonly totalQuestions: number;
};

const Navbar = styled.div`
  padding: ${SPACING.STATIC.MD.rem};
  background: ${COLORS.BACKGROUND_COLORS.WHITE};
  box-shadow:
    0 0 1px 0 rgba(195, 201, 207, 0.1),
    0 1px 5px 0 rgba(195, 201, 207, 0.3);
  width: 100%;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoContainer = styled.a`
  width: 115px;
  height: 33px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledInputTextArea = styled(InputTextArea)`
  max-width: 512px;
`;

const StyledButton = styled(Button)<{ isSMScreenOrBigger: boolean }>`
  width: 159px;
  margin-bottom: ${SPACING.STATIC.XS.rem};
  ${({ isSMScreenOrBigger }) => isSMScreenOrBigger && `width: 189px;`}
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const PageContainer = styled(FlexRow).attrs({
  spacing: SPACING.STATIC.LG.rem,
})<{ isSMScreenOrBigger: boolean; isLGScreenOrBigger: boolean }>`
  padding: ${spacing.xlSpacing} ${SPACING.STATIC.LG.rem} ${SPACING.STATIC.LG.rem} ${SPACING.STATIC.LG.rem};
  width: 1170px;
  margin: 0 auto;

  ${({ isSMScreenOrBigger }) => !isSMScreenOrBigger && `min-width: 240px;`}
  ${({ isLGScreenOrBigger }) => !isLGScreenOrBigger && `width: auto;`}
`;

const ContentContainer = styled(FlexRow).attrs({
  spacing: SPACING.STATIC.LG.rem,
})<{ isSMScreenOrBigger: boolean }>`
  min-height: 354px;
  ${({ isSMScreenOrBigger }) => !isSMScreenOrBigger && `min-height: 432px;`}
`;

const generateInitialValues = (numValues: number): FormValues => {
  const initialValues = {};

  for (let i = 0; i < numValues; i++) {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expression of type 'number' can't be used to index type... (Delete me to see the full error)
    initialValues[i] = null;
  }

  return initialValues;
};

// @ts-expect-error TS(7031): Binding element 'onChange' implicitly has an 'any' type.
// eslint-disable-next-line react/prop-types
const NumericQuestion = ({ onChange, value }) => {
  return (
    <RadioGroup
      input={{
        name: 'numericQuestion',
        onChange,
        value,
      }}
    >
      <RadioOption value="1">
        <Text small>1 — Strongly disagree</Text>
      </RadioOption>
      <RadioOption value="2">
        <Text small>2 — Disagree</Text>
      </RadioOption>
      <RadioOption value="3">
        <Text small>3 — Neutral</Text>
      </RadioOption>
      <RadioOption value="4">
        <Text small>4 — Agree</Text>
      </RadioOption>
      <RadioOption value="5">
        <Text small>5 — Strongly agree</Text>
      </RadioOption>
    </RadioGroup>
  );
};

// @ts-expect-error TS(7031): Binding element 'onChange' implicitly has an 'any' type.
// eslint-disable-next-line react/prop-types
const TextQuestion = ({ onChange, value }) => {
  return (
    <StyledInputTextArea
      input={{
        onChange,
        value,
        placeholder: 'I think Alto could...',
      }}
    />
  );
};

export default class SurveyPage extends PureComponent<Props> {
  render() {
    const { changeStep, onSubmit, question, step, totalQuestions } = this.props;
    // eslint-disable-next-line import/no-deprecated
    const { isSMScreenOrBigger, isLGScreenOrBigger } = getScreenSize();

    const currentQuestion = question || {};
    const initialValues = generateInitialValues(totalQuestions);
    const isRequiredQuestion = !!currentQuestion.required;
    const isNumericTypeQuestion = currentQuestion.type === 'numeric';
    const isTextTypeQuestion = currentQuestion.type === 'text';
    const buttonTitle = step === totalQuestions - 1 ? 'Finish' : 'Next';
    const completed = step === totalQuestions;

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ values, handleChange, handleSubmit }) => (
          <div>
            <Navbar>
              <LogoContainer
                href="https://alto.com"
                aria-label="Alto Logo"
              >
                <Image src={logoAlto} />
              </LogoContainer>
            </Navbar>
            <PageContainer
              isSMScreenOrBigger={isSMScreenOrBigger}
              isLGScreenOrBigger={isLGScreenOrBigger}
            >
              <ContentContainer isSMScreenOrBigger={isSMScreenOrBigger}>
                <FlexRow spacing={SPACING.STATIC.MD.rem}>
                  <PageHeader underline={false}>How was your experience?</PageHeader>
                  {isNumericTypeQuestion ? (
                    <Text small>To what extent do you agree or disagree with the following statement:</Text>
                  ) : null}
                </FlexRow>
                {!completed && (
                  <FlexRow spacing={SPACING.STATIC.XXS.rem}>
                    <Label light>
                      QUESTION {step + 1} OF {totalQuestions}
                    </Label>
                    <H3>
                      {currentQuestion.question}
                      {isRequiredQuestion ? (
                        <Text
                          bold
                          danger
                          inline
                          short
                        >
                          *
                        </Text>
                      ) : null}
                    </H3>
                    {isNumericTypeQuestion ? (
                      <NumericQuestion
                        onChange={handleChange(step.toString())}
                        value={values[step]}
                      />
                    ) : null}
                    {isTextTypeQuestion ? (
                      <TextQuestion
                        onChange={handleChange(step.toString())}
                        value={values[step]}
                      />
                    ) : null}
                  </FlexRow>
                )}
                {completed ? (
                  <FlexRow spacing={SPACING.STATIC.XXS.rem}>
                    <H3>Thanks for your feedback!</H3>
                    <Text small>
                      We really appreciate you taking the time to complete this survey and helping us improve the Alto
                      experience.
                    </Text>
                  </FlexRow>
                ) : null}
              </ContentContainer>
              {!completed && (
                <FlexColumn spacing={SPACING.STATIC.XS.rem}>
                  {step !== 0 && (
                    <StyledButton
                      kind="secondary"
                      onClick={() => {
                        changeStep('back');
                      }}
                      isSMScreenOrBigger={isSMScreenOrBigger}
                    >
                      Previous
                    </StyledButton>
                  )}
                  <StyledButton
                    isSMScreenOrBigger={isSMScreenOrBigger}
                    disabled={isRequiredQuestion ? !values[step] : false}
                    onClick={() => {
                      handleSubmit();
                      changeStep();
                    }}
                  >
                    {buttonTitle}
                  </StyledButton>
                </FlexColumn>
              )}
            </PageContainer>
          </div>
        )}
      </Formik>
    );
  }
}
